/**
 * FluentCart Integration — Composition Root
 *
 * Lightweight orchestrator that wires together:
 * - State management (fluentcart-state-manager)
 * - DOM scraping (fluentcart-dom-scraper)
 * - Modal UI controller (fluentcart-modal-controller)
 * - Modal routing (modal-router)
 * - Checkout pipeline (fluentcart-checkout-orchestrator)
 */

import { fetchEligibility, fetchUnfulfilledDonation } from './shared/api';
import { isLoggedIn } from './shared/auth-state';
import { buildReturnStatusUrl, type ReturnStatusUrlOptions } from './shared/routing';
import {
  route as routeModal,
  ENTRY_POINT,
  type EntryPoint,
  type ModalContext,
  type MagicLinkContext,
} from './modal-router';
import { cacheDomElements } from './fluentcart/fluentcart-dom';
import { getFluentCartModalHtml } from './fluentcart/fluentcart-template';
import { clearDemoCheckoutFields, setStatus as setStatusForElements } from './fluentcart/fluentcart-renderer';
import { bindDonateTriggers, bindVoteTriggers } from './fluentcart/fluentcart-event-bindings';
import { markFluentCartReady } from './fluentcart/fluentcart-readiness';
import { cleanFluentCartUrlParams, parseFluentCartUrlParams } from './fluentcart/fluentcart-url-parser';
import { logClientEvent } from './fluentcart/fluentcart-telemetry';
import type { FluentCartModalContext } from './fluentcart/fluentcart-modal-context';
import { castBaseVoteAndOptionallyDonate, castRetroactiveAndSubmit, castRetroactiveVote, castStandardVote, submitDonation, type FluentCartCheckoutDeps } from './fluentcart/fluentcart-checkout-orchestrator';
import { createFluentCartStore, resolveMonthlyAmount, type FluentCartStore } from './fluentcart/fluentcart-state-manager';
import { getCharityCardContext } from './fluentcart/fluentcart-dom-scraper';
import { renderModal, bindModalEvents } from './fluentcart/fluentcart-modal-controller';
import type { DemoCheckoutPurpose, FluentCartCheckoutState, FluentCartElements } from '../types/pifster-contracts';
import { clearDemoSessionCache, fetchDemoSession } from './demo/demo-api';
import { needsLiveEligibilityResolution as shouldResolveLiveEligibility } from './demo/demo-session-routing';
import { buildDemoModalContext } from './fluentcart/fluentcart-demo-context';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MODAL_ID = 'pifster-fluentcart-modal';
const AUTO_OPEN_SUPPRESS_KEY = 'pifster_suppress_until';
const AUTO_OPEN_SUPPRESS_DURATION_MS = 30000;

const ROUTER_BYPASS_ENTRIES = new Set<FluentCartModalContext['entry']>([
  'header_join',
  'return_status_cta',
  'gift_purchase',
  'corporate_checkout',
]);

function suppressAutoOpen(): void {
  try {
    localStorage.setItem(AUTO_OPEN_SUPPRESS_KEY, String(Date.now() + AUTO_OPEN_SUPPRESS_DURATION_MS));
  } catch {
    // no-op
  }
}

function isAutoOpenSuppressed(): boolean {
  try {
    const cutoff = localStorage.getItem(AUTO_OPEN_SUPPRESS_KEY);
    if (!cutoff) {
      return false;
    }
    return Date.now() < Number.parseInt(cutoff, 10);
  } catch {
    return false;
  }
}

function clearAutoOpenSuppress(): void {
  try {
    localStorage.removeItem(AUTO_OPEN_SUPPRESS_KEY);
  } catch {
    // no-op
  }
}

// ---------------------------------------------------------------------------
// Module-level singletons
// ---------------------------------------------------------------------------

let store: FluentCartStore;
let modalEl: HTMLElement | null = null;
let elements: FluentCartElements | null = null;
let eventsBound = false;

// ---------------------------------------------------------------------------
// Modal Lifecycle
// ---------------------------------------------------------------------------

function ensureModal(): HTMLElement {
  if (modalEl) {
    return modalEl;
  }

  const existingModal = document.getElementById(MODAL_ID);
  if (existingModal) {
    modalEl = existingModal;
  } else {
    const createdModal = document.createElement('div');
    createdModal.id = MODAL_ID;
    createdModal.className = 'c-modal pifster-donation-modal';
    createdModal.setAttribute('role', 'dialog');
    createdModal.setAttribute('aria-modal', 'true');
    createdModal.setAttribute('aria-labelledby', 'pifster-donation-modal-title');
    createdModal.hidden = true;
    createdModal.innerHTML = getFluentCartModalHtml();
    document.body.appendChild(createdModal);
    modalEl = createdModal;
  }

  if (!modalEl) {
    throw new Error('FluentCart integration could not create the modal container.');
  }

  elements = cacheDomElements(modalEl);

  if (!eventsBound) {
    eventsBound = true;
    bindModalEvents(modalEl, elements, store, {
      closeModal,
      switchToVotingFlow,
      submitDonation(): void {
        suppressAutoOpen();
        void submitDonation(store.state, store.config, store.config.productIds, createCheckoutDeps());
      },
      castStandardVote(): void {
        suppressAutoOpen();
        void castStandardVote(store.state, store.config.apiBase, store.getBootstrapNonce(), createCheckoutDeps());
      },
      castBaseVoteAndSubmit(): void {
        suppressAutoOpen();
        void castBaseVoteAndOptionallyDonate(
          store.state,
          store.config,
          store.config.productIds,
          store.config.apiBase,
          store.getBootstrapNonce(),
          createCheckoutDeps(),
        );
      },
      castRetroactiveVote(): void {
        suppressAutoOpen();
        void castRetroactiveVote(store.state, store.config.apiBase, store.getBootstrapNonce(), createCheckoutDeps());
      },
      castRetroactiveAndSubmit(): void {
        suppressAutoOpen();
        void castRetroactiveAndSubmit(
          store.state,
          store.config,
          store.config.productIds,
          store.config.apiBase,
          store.getBootstrapNonce(),
          createCheckoutDeps(),
        );
      },
    });
  }

  return modalEl;
}

function showModal(): void {
  ensureModal();

  if (!modalEl) {
    throw new Error('FluentCart modal is unavailable.');
  }

  modalEl.hidden = false;
  const dialog = modalEl.querySelector<HTMLDivElement>('.c-modal__dialog');
  if (dialog) {
    dialog.setAttribute('tabindex', '-1');
    dialog.focus();
  }

  document.body.style.overflow = 'hidden';
}

function closeModal(): void {
  if (!modalEl) {
    return;
  }

  if (!modalEl.hidden) {
    logClientEvent(store.getSnapshot(), 'modal_closed', {});
  }

  modalEl.hidden = true;
  document.body.style.overflow = '';
}

function createCheckoutDeps(): FluentCartCheckoutDeps {
  if (!elements) {
    throw new Error('FluentCart modal elements have not been cached.');
  }

  const modalElements = elements;

  return {
    setStatus(message: string): void {
      setStatusForElements(modalElements, message);
    },
    setButtonsDisabled(disabled: boolean): void {
      if (modalElements.primaryButtonEl) {
        modalElements.primaryButtonEl.disabled = disabled;
      }
      if (modalElements.secondaryButtonEl) {
        modalElements.secondaryButtonEl.disabled = disabled;
      }
    },
    logEvent(name: string, payload: unknown): void {
      logClientEvent(store.getSnapshot(), name, payload as Record<string, unknown> | undefined);
    },
    redirectTo(url: string): void {
      window.location.href = url;
    },
    buildReturnStatusUrl(options: ReturnStatusUrlOptions): string {
      return buildReturnStatusUrl(store.getReturnStatusBaseUrl(), options);
    },
    showDemoCheckout(purpose: DemoCheckoutPurpose): void {
      store.state.demoCheckoutPurpose = purpose;
      renderModal(store, modalElements);
    },
    isDemoCheckoutComplete(): boolean {
      return [
        modalElements.demoCardNumberEl?.value.trim() ?? '',
        modalElements.demoExpiryEl?.value.trim() ?? '',
        modalElements.demoCvcEl?.value.trim() ?? '',
      ].every((value) => value !== '');
    },
    onDemoMembershipComplete(): void {
      clearDemoSessionCache();
      store.state.demoCheckoutPurpose = null;
      store.state.eligible = true;
      store.state.frequency = 'one_time';
      store.state.givingCadence = null;
      store.state.amount = Math.max(1, store.config.minAmount);
      renderModal(store, modalElements);
    },
    clearDemoCheckoutFields(): void {
      clearDemoCheckoutFields(modalElements);
    },
    closeModal(): void {
      closeModal();
    },
  };
}

// ---------------------------------------------------------------------------
// Open / Route Logic
// ---------------------------------------------------------------------------

function showResolvedModal(context: Partial<FluentCartModalContext>, previousState: FluentCartCheckoutState, previousModalVisible: boolean): void {
  // After eligibility/unfulfilled-donation resolution the resolved state may be
  // different from what initializeModalState first computed. Re-apply the
  // per-state defaults (including the $0 start for VOTE_WITH_EXISTING_DONATION)
  // before rendering.
  const stateConfig = store.syncResolvedState();
  const { state } = store;
  state.minAmount = stateConfig.minAmount;
  state.frequency = stateConfig.defaultFrequency;
  state.givingCadence = stateConfig.showTierSelector ? stateConfig.defaultCadence : null;
  const baseAmount = Math.max(stateConfig.minAmount, 0);
  state.amount = state.frequency === 'monthly' && stateConfig.showTierSelector
    ? resolveMonthlyAmount(state.givingCadence, baseAmount)
    : baseAmount;

  if (elements) {
    renderModal(store, elements);
  }

  showModal();
  logClientEvent(previousState, 'modal_opened', {
    requestedEntry: context.entry ?? 'standard',
    requestedMode: context.mode ?? 'vote_plus_donate',
    previousTraceId: previousState.clientTraceId || '',
    previousFrequency: previousState.frequency || '',
    previousAmount: previousState.amount,
    previousResolvedState: previousState.resolvedState || '',
    previousModalVisible,
  });
}

export function needsLiveEligibilityResolution(state: FluentCartCheckoutState): boolean {
  return shouldResolveLiveEligibility(state, isLoggedIn());
}

async function openModal(context: Partial<FluentCartModalContext>): Promise<void> {
  const demoSession = await fetchDemoSession();
  const resolvedContext = demoSession ? buildDemoModalContext(context, demoSession) : context;
  const previousModalVisible = modalEl ? modalEl.hidden === false : false;
  const previousState = store.getSnapshot();
  ensureModal();
  store.initializeModalState(resolvedContext);

  const needsResolution = needsLiveEligibilityResolution(store.state);

  if (!needsResolution) {
    showResolvedModal(resolvedContext, previousState, previousModalVisible);
    return;
  }

  const requestElements = elements;
  const clientTraceId = store.state.clientTraceId;
  const nonce = store.getBootstrapNonce();

  function isStaleRequest(): boolean {
    return store.state.clientTraceId !== clientTraceId || requestElements !== elements;
  }

  try {
    const eligibility = await fetchEligibility(store.state.periodId as number, nonce);
    if (isStaleRequest()) {
      return;
    }

    if (!eligibility) {
      logClientEvent(store.getSnapshot(), 'eligibility_resolution_skipped', {
        reason: 'no_response',
      });
      showResolvedModal(resolvedContext, previousState, previousModalVisible);
      return;
    }

    store.state.eligible = eligibility.eligible === true;
    store.state.alreadyVoted = eligibility.alreadyVoted === true;
    if (typeof eligibility.votedCharityId === 'number') {
      store.state.votedCharityId = eligibility.votedCharityId;
    }
    if (typeof eligibility.cumulativeBonusVotes === 'number' && Number.isFinite(eligibility.cumulativeBonusVotes)) {
      store.state.cumulativeBonusVotes = Math.max(0, eligibility.cumulativeBonusVotes);
    }
    store.state.reasons = Array.isArray(eligibility.reasons) ? [...eligibility.reasons] : [];
    store.state.reason = typeof eligibility.reason === 'string' ? eligibility.reason : '';

    const shouldFetchUnfulfilled =
      store.state.eligible &&
      !store.state.alreadyVoted &&
      store.state.charityId !== null &&
      store.state.periodId !== null;

    if (!shouldFetchUnfulfilled) {
      showResolvedModal(resolvedContext, previousState, previousModalVisible);
      logClientEvent(store.getSnapshot(), 'eligibility_resolved', {
        reasonsCount: store.state.reasons.length,
      });
      return;
    }

    try {
      const donation = await fetchUnfulfilledDonation(
        store.state.charityId as number,
        store.state.periodId as number,
        nonce,
      );
      if (isStaleRequest()) {
        return;
      }

      store.state.unfulfilledDonation = donation;
      showResolvedModal(resolvedContext, previousState, previousModalVisible);
      logClientEvent(store.getSnapshot(), 'eligibility_resolved', {
        reasonsCount: store.state.reasons.length,
        hasUnfulfilledDonation: donation !== null,
      });
    } catch (error: unknown) {
      console.error('PIFster Critical: Unfulfilled donation lookup failed', error);
      if (isStaleRequest()) {
        return;
      }
      showResolvedModal(resolvedContext, previousState, previousModalVisible);
      logClientEvent(store.getSnapshot(), 'unfulfilled_donation_lookup_failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  } catch (error: unknown) {
    console.error('PIFster Critical: FluentCart eligibility lookup failed', error);
    if (isStaleRequest()) {
      return;
    }
    showResolvedModal(resolvedContext, previousState, previousModalVisible);
    logClientEvent(store.getSnapshot(), 'eligibility_resolution_failed', {
      error: error instanceof Error ? error.message : String(error),
    });
  }
}

function switchToVotingFlow(): void {
  const { state, config } = store;

  if (state.charityId === null || state.periodId === null) {
    closeModal();
    return;
  }

  const context: Partial<FluentCartModalContext> = {
    charityId: state.charityId,
    charityName: state.charityName || '',
    periodId: state.periodId,
    intentToken: state.intentToken || null,
    beneficiary: state.beneficiary,
    donateHrefFallback: state.donateHrefFallback || '',
    votedCharityId: state.votedCharityId,
    cumulativeBonusVotes: state.cumulativeBonusVotes,
    bonusRatio: config.bonusRatio,
    bonusCap: config.bonusCap,
    mode: 'vote_plus_donate',
    entry: 'standard',
  };

  closeModal();
  maybeOpenModal(context);
}

function openDonationFromRouterContext(routerContext: ModalContext): Promise<void> {
  const fluentCartMode: FluentCartModalContext['mode'] =
    routerContext.mode === 'donate_only' ? 'donate_only' : 'vote_plus_donate';
  return openModal(getCharityCardContext({ ...routerContext, mode: fluentCartMode }));
}

function maybeOpenModal(context: Partial<FluentCartModalContext> | null | undefined): Promise<void> {
  if (!context) {
    return Promise.resolve();
  }

  const isDonateOnly = context.mode === 'donate_only';
  const mode: 'vote_plus_donate' | 'donate_only' = isDonateOnly ? 'donate_only' : 'vote_plus_donate';
  const entry: string = context.entry ?? 'standard';
  const needsRouter = mode === 'vote_plus_donate'
    && store.config.testMode !== true
    && !ROUTER_BYPASS_ENTRIES.has(entry);

  logClientEvent(store.getSnapshot(), 'modal_route_decision', {
    requestedEntry: entry,
    requestedMode: mode,
    needsRouter,
    routerBypassEntry: ROUTER_BYPASS_ENTRIES.has(entry),
  });

  if (!needsRouter) {
    return openModal(context);
  }

  let entryPoint: EntryPoint = ENTRY_POINT.VOTE_BUTTON;
  if (entry === 'complete_vote') {
    entryPoint = ENTRY_POINT.COMPLETE_VOTE;
  } else if (isDonateOnly || entry === 'donate_button') {
    entryPoint = ENTRY_POINT.DONATE_BUTTON;
  }

  return routeModal(
    {
      charityId: context.charityId ?? null,
      charityName: context.charityName ?? '',
      periodId: context.periodId ?? null,
      intentToken: context.intentToken ?? null,
      beneficiary: context.beneficiary ?? null,
      donateHrefFallback: context.donateHrefFallback ?? '',
      bonusRatio: typeof context.bonusRatio === 'number' ? context.bonusRatio : undefined,
      bonusCap: typeof context.bonusCap === 'number' ? context.bonusCap : undefined,
    },
    entryPoint,
    {
      openDonationModal: openDonationFromRouterContext,
      openMagicLinkModal(voteContext: MagicLinkContext): void {
        logClientEvent(store.getSnapshot(), 'magic_link_requested', {
          voteCharityId: voteContext.charityId,
          votePeriodId: voteContext.periodId,
        });

        document.dispatchEvent(new CustomEvent('pifster:open-vote-intent', {
          detail: {
            charityId: String(voteContext.charityId ?? ''),
            charityName: voteContext.charityName,
            periodId: String(voteContext.periodId ?? ''),
          },
        }));
      },
      redirectToReturnStatus(routerContext: ModalContext): void {
        const isCompleteVoteFlow = routerContext.entryPoint === ENTRY_POINT.COMPLETE_VOTE;
        window.location.href = buildReturnStatusUrl(store.getReturnStatusBaseUrl(), {
          action: 'vote',
          status: isCompleteVoteFlow ? 'success' : 'already_voted',
          charityId: routerContext.charityId,
          periodId: routerContext.periodId,
          alreadyVoted: !isCompleteVoteFlow && routerContext.alreadyVoted,
        });
      },
    },
  );
}

// ---------------------------------------------------------------------------
// URL Auto-Open & Events
// ---------------------------------------------------------------------------

function autoOpenFromUrl(): void {
  if (window.location.pathname.includes('/return-status')) {
    return;
  }

  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('pif_status')) {
    return;
  }

  if (isAutoOpenSuppressed()) {
    return;
  }

  const urlContext = parseFluentCartUrlParams(window.location.href);
  if (!urlContext) {
    return;
  }

  const cleanUrl = new URL(window.location.href);
  cleanFluentCartUrlParams(cleanUrl);
  window.history.replaceState({}, '', cleanUrl.toString());

  maybeOpenModal(getCharityCardContext(urlContext));
}

function openFluentCartFromEvent(event: Event): void {
  const customEvent = event as CustomEvent<Partial<FluentCartModalContext>>;
  if (!customEvent.detail) {
    throw new Error('FluentCart open-modal event is missing its context payload.');
  }

  maybeOpenModal(customEvent.detail);
}

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------

function boot(): void {
  markFluentCartReady(document.documentElement);
  ensureModal();
  bindDonateTriggers(maybeOpenModal);
  bindVoteTriggers(maybeOpenModal);
  document.addEventListener('pifster:open-fluentcart-modal', openFluentCartFromEvent);
  autoOpenFromUrl();
}

store = createFluentCartStore();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

export { boot, maybeOpenModal, openFluentCartFromEvent };
